import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { PageLayout } from '../../PageLayout';
import { PageCard } from '../../Compounds/CardWithTitle';
import { Text } from '../../Atoms/Text';
import { TabPanel } from '../../Atoms/TabPanel';
import { GridTabsHeader } from '../../Compounds/GridTabsHeader';
import { UpstackDataGrid } from '../../Compounds/UpstackDataGrid/UpstackDataGrid';
import { getGridColumns } from '../../Compounds/UpstackDataGrid/helpers';
import { ServiceRequestLink } from '../../Compounds/ServiceRequest/ServiceRequestLink';
import { WorkflowPipelineSummary } from '../../Compounds/WorkflowPipelineSummary';
import { SavedViewControls } from '../../Compounds/CustomTableViews/SavedViewControls';
import { ViewsModal } from '../../Compounds/CustomTableViews/ViewsModal';
import { NewViewModal } from '../../Compounds/CustomTableViews/NewViewModal';
import { IncludedTabsField } from '../../Compounds/OrdersScheduleReport/IncludedTabsField';
import { FeatureFlags, getFeatureFlagSubject } from '../../../Api/useFeatureFlags';
import { useWorkflowOrders } from '../../../store/WorkflowOrder';
import { WorkflowOrder, WorkflowTab } from '../../../Api/Orders/ordersApi';
import {
  getDefaultPreferences,
  getPreferencesByKey,
  getSelectedAccountsSubject,
  getUser,
  getUserPreferencesSubject,
  GridPreferences,
  isOrdersScheduledReportsEnabled,
  isQuoteApprovalEnabled,
  OrderRequestPreferences,
  PrefKey
} from '../../../store/User';
import { WorkflowColumnsWithoutDetails, WorkflowColumnsWithDetails } from './WorkflowOrdersGridColumnDefs';
import { WORKFLOW_TAB_LABELS, WORKFLOW_TAB_ORDER } from './workflowTabs';
import { sameJson } from '../../../utils/sameJson';
import { useOrdersViewTabs, viewStages } from './useOrdersSavedViews';

const tabLabel = (label: string, count: number) => `${label} (${count})`;

const headerContent = (rowCount: number, displayedRowCount: number | undefined) => {
  if (!rowCount) return '';
  const base = `${rowCount} Item${rowCount > 1 ? 's' : ''}`;
  if (rowCount === displayedRowCount || displayedRowCount === undefined) return base;
  return `${displayedRowCount} of ${base}`;
};

export const displayedRowCountContent = (rowCount: number, displayedRowCount: number | undefined) => (
  <Text
    dataTestId="order-count"
    className="font-medium grow ml-4"
    size="sm14"
    color="grey4">
    {headerContent(rowCount, displayedRowCount)}
  </Text>
);

export const OrdersPage = () => {
  const page = 'Orders';
  const location = useLocation();
  // Each Save As View is numbered and tied to its grid, so one dropped or replaced opens nothing when its save lands.
  const [saveAttempt, setSaveAttempt] = useState<{ id: number; page: PrefKey } | null>(null);
  const latestSaveAttempt = useRef(0);
  const triggerSave = saveAttempt !== null;
  const [viewsOpen, setViewsOpen] = useState(false);
  const [newViewOpen, setNewViewOpen] = useState(false);
  const [newViewStages, setNewViewStages] = useState<WorkflowTab[]>([]);
  const [featureFlags, setFeatureFlags] = useState<FeatureFlags>(getFeatureFlagSubject().getValue());
  const ordersReportsEnabled = isOrdersScheduledReportsEnabled(featureFlags);
  const [orderRequestPrefs, setOrderRequestPrefs] = useState<OrderRequestPreferences | undefined>(
    getPreferencesByKey('order_request') as OrderRequestPreferences | undefined
  );
  const [gridSettings, setGridSettings] = useState<GridPreferences>(
    getDefaultPreferences().content['order_grid'] as GridPreferences
  );

  const { orders, loading, fetchWorkflowOrders } = useWorkflowOrders();
  const { views, activeTab, selectedView, panelIndex, rowsByView, selectTab, selectView } = useOrdersViewTabs(
    ordersReportsEnabled,
    orders
  );

  useEffect(() => {
    fetchWorkflowOrders();
    const accountSub = getSelectedAccountsSubject().subscribe(() => {
      fetchWorkflowOrders(true);
    });
    return () => accountSub.unsubscribe();
  }, []);

  useEffect(() => {
    const settingsSub = getUserPreferencesSubject().subscribe(() => {
      const reqPrefs = getPreferencesByKey('order_request') as OrderRequestPreferences | undefined;
      if (reqPrefs) setOrderRequestPrefs(reqPrefs);
      const grid = getPreferencesByKey('order_grid') as GridPreferences | undefined;
      // A save of any other key re-emits this one too, and a fresh copy would reset the grid's unsaved layout.
      if (grid) setGridSettings((current) => (sameJson(current, grid) ? current : grid));
    });
    const featureFlagsSub = getFeatureFlagSubject().subscribe((flags) => {
      setFeatureFlags(flags);
      // Otherwise the dialogs would open by themselves when the flag comes back on.
      if (!isOrdersScheduledReportsEnabled(flags)) {
        dropSaveAsView();
        setViewsOpen(false);
        setNewViewOpen(false);
      }
    });
    return () => {
      settingsSub.unsubscribe();
      featureFlagsSub.unsubscribe();
    };
  }, []);

  // Deep-link support: a `?tab=` query param (e.g. from an order notification) selects that tab.
  // Keyed on location.key (not just the search string) so a repeat notification click to the same
  // URL re-applies the tab even after the user has manually switched tabs in between.
  useEffect(() => {
    const tab = new URLSearchParams(location.search).get('tab');
    if (!tab) return;
    const idx = WORKFLOW_TAB_ORDER.indexOf(tab as WorkflowTab);
    if (idx >= 0) showTab(idx);
  }, [location.key, location.search]);

  const showDetailsColumn =
    isQuoteApprovalEnabled(orderRequestPrefs, getUser()) || orders.some((order) => order.quote_review_available);
  // Route through getGridColumns (like every other grid page) so singleSelect columns get
  // their valueOptions populated from the data — otherwise the AutoComplete filter is empty.
  const columns = useMemo(
    () =>
      getGridColumns(
        orders,
        showDetailsColumn ? WorkflowColumnsWithDetails : WorkflowColumnsWithoutDetails,
        'order_grid'
      ),
    [orders, showDetailsColumn]
  );

  const ordersByTab = useMemo(() => {
    const grouped: Record<WorkflowTab, WorkflowOrder[]> = {
      submitted: [],
      quoting_in_progress: [],
      reviewing_approving_quotes: [],
      upstack_processing_order: [],
      hand_off_to_pm: []
    };
    orders.forEach((o) => {
      if (grouped[o.workflow_tab]) grouped[o.workflow_tab].push(o);
    });
    return grouped;
  }, [orders]);

  const totalCount = WORKFLOW_TAB_ORDER.reduce((sum, tab) => sum + ordersByTab[tab].length, 0);

  const openPage: PrefKey = selectedView?.key ?? 'order_grid';

  const startSaveAsView = () => {
    latestSaveAttempt.current += 1;
    setSaveAttempt({ id: latestSaveAttempt.current, page: openPage });
  };

  const dropSaveAsView = () => {
    latestSaveAttempt.current += 1;
    setSaveAttempt(null);
  };

  // Another panel drops a Save As View in flight, or its dialog would pair one panel's tabs with another's layout.
  const showTab = (index: number) => {
    dropSaveAsView();
    selectTab(index);
  };

  const showView = (name: string) => {
    dropSaveAsView();
    selectView(name);
  };

  // Runs on every change of open view, mount included: a Save As View or dialog begun on another view is void here.
  useEffect(() => {
    dropSaveAsView();
    setNewViewOpen(false);
  }, [selectedView?.key]);

  const openNewView = (attempt?: number) => {
    if (attempt !== latestSaveAttempt.current) return;
    dropSaveAsView();
    setNewViewStages(selectedView ? viewStages(selectedView) : [WORKFLOW_TAB_ORDER[activeTab]]);
    setNewViewOpen(true);
  };

  const ordersGrid = (title: string, rows: WorkflowOrder[], page: PrefKey, settings: GridPreferences) => (
    <UpstackDataGrid
      dataCy="orders-data-grid"
      title={title}
      rows={rows}
      columns={columns}
      loadingData={loading}
      page={page}
      gridSettings={settings}
      showSearch={false}
      pinnedColumns={showDetailsColumn ? { left: ['details'] } : undefined}
      triggerSave={saveAttempt?.page === page}
      handleNewTabModalOpen={() => openNewView(saveAttempt?.id)}
    />
  );

  return (
    <PageLayout pageTitle={page}>
      <WorkflowPipelineSummary
        ordersByTab={ordersByTab}
        totalCount={totalCount}
        activeTab={panelIndex}
        onTabSelect={showTab}
        newRequestAction={
          <ServiceRequestLink
            ctaClassName="text-white text-sm font-medium bg-[#3B6FE8] rounded-lg px-5 py-2.5 hover:bg-[#2F5CC0]"
            label="New Request"
            showIcon={false}
          />
        }
      />
      <PageCard
        childrenWrapperClass="min-h-80 max-w-[60vw] min-w-full"
        title="Requests">
        <GridTabsHeader
          dataCy="orders-tabs-header"
          labels={WORKFLOW_TAB_ORDER.map((tab) => tabLabel(WORKFLOW_TAB_LABELS[tab], ordersByTab[tab].length))}
          getTabTestId={(index) => `orders-tab-${WORKFLOW_TAB_ORDER[index]}`}
          selectedTab={panelIndex}
          onTabChange={showTab}>
          {ordersReportsEnabled && (
            <SavedViewControls
              selectedCustomView={selectedView?.name ?? ''}
              customTabs={views.map((view) => view.name)}
              onSelectView={showView}
              onSaveAsView={startSaveAsView}
              saving={triggerSave}
              onScheduleReport={() => setViewsOpen(true)}
            />
          )}
        </GridTabsHeader>
        {WORKFLOW_TAB_ORDER.map((tab, idx) => (
          <TabPanel
            key={tab}
            value={panelIndex}
            index={idx}>
            {ordersGrid(WORKFLOW_TAB_LABELS[tab], ordersByTab[tab], 'order_grid', gridSettings)}
          </TabPanel>
        ))}
        {views.map((view, idx) => (
          <TabPanel
            key={view.key}
            value={panelIndex}
            index={WORKFLOW_TAB_ORDER.length + idx}>
            {ordersGrid(view.name, rowsByView.get(view.key) ?? [], view.key, view.settings)}
          </TabPanel>
        ))}
      </PageCard>
      <div className="h-2"></div>
      {ordersReportsEnabled && (
        <>
          <ViewsModal
            open={viewsOpen}
            handleClose={() => setViewsOpen(false)}
            type="order"
          />
          <NewViewModal
            open={newViewOpen}
            setOpen={setNewViewOpen}
            type="order"
            sourceTab={selectedView?.name ?? WORKFLOW_TAB_ORDER[activeTab]}
            sourceKey={openPage}
            viewFields={{ included_stages: newViewStages }}
            canSave={newViewStages.length > 0}>
            <IncludedTabsField
              value={newViewStages}
              disabled={false}
              onChange={setNewViewStages}
            />
          </NewViewModal>
        </>
      )}
    </PageLayout>
  );
};
